import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { createNewLoan } from "@api/gpf";

import { Button } from "@pentabd/ui";

import {
  ErrorModal,
  InputDateControllerRow,
  InputTextRow,
  MultiFileRow,
  SuccessModal,
} from "@components";

import { ROUTES } from "@constants/routes";

import {
  AddNewLoanFormDataType,
  CREATE_NEW_LOAN,
  addNewLoanValidation,
} from "@validations/HRM/GPF/createNewLoanValidation";
import { convertMultipleDocumentData } from "@utils/documentDataConvert";

const {
  HRM: { PROVIDENT_FUND },
} = ROUTES;

interface CreateNewLoanFormType {
  id: string | undefined;
}

export const CreateNewLoanForm = ({ id }: CreateNewLoanFormType) => {
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);

  const { t } = useTranslation();

  const closeErrorModal = () => {
    setIsErrorModalOpen(false);
  };

  const methods = useForm<AddNewLoanFormDataType>({
    resolver: yupResolver(addNewLoanValidation),
  });

  const {
    handleSubmit,
    register,
    control,
    setValue,
    formState: { errors },
  } = methods;

  const onSubmit: SubmitHandler<AddNewLoanFormDataType> = (data: any) => {
    //!!! used any
    createNewLoan(
      id,
      convertMultipleDocumentData(data, [
        `${CREATE_NEW_LOAN.LOAN_APPLICATION}`,
        `${CREATE_NEW_LOAN.DOCUMENT}`,
      ]),
      ({ status }) => {
        if (status === "success") {
          setIsSuccessModalOpen(true);
        } else setIsErrorModalOpen(true);
      },
    );
  };

  useEffect(() => {
    const reset = () => {
      setIsErrorModalOpen(false);
      setIsSuccessModalOpen(false);
    };
    return reset();
  }, []);

  return (
    <>
      {/* MODAL SECTION */}
      <SuccessModal
        isModalOpen={isSuccessModalOpen}
        redirectPath={PROVIDENT_FUND}
        subtitle="You have created a new General Provident Fund Loan"
      />
      <ErrorModal isModalOpen={isErrorModalOpen} closeModal={closeErrorModal} />

      <form className="mt-10" onSubmit={handleSubmit(onSubmit)}>
        <div className="d-flex flex-column gap-8 pb-8">
          <InputTextRow
            register={{ ...register(CREATE_NEW_LOAN.LOAN_ID) }}
            label="CREATE_NEW_LOAN.FORM.LOAN_ID"
            error={errors}
            required
          />
          <InputTextRow
            register={{ ...register(CREATE_NEW_LOAN.LOAN_AMOUNT) }}
            label="CREATE_NEW_LOAN.FORM.LOAN_AMOUNT"
            error={errors}
            required
          />
          <InputDateControllerRow
            label="CREATE_NEW_LOAN.FORM.LOAN_ISSUE_DATE"
            control={control}
            register={{ ...register(CREATE_NEW_LOAN.LOAN_ISSUE_DATE) }}
            errors={errors}
            name={CREATE_NEW_LOAN.LOAN_ISSUE_DATE}
            required
          />

          <MultiFileRow
            label="CREATE_NEW_LOAN.FORM.LOAN_APPLICATION"
            name={CREATE_NEW_LOAN.LOAN_APPLICATION}
            control={control}
            setValue={setValue}
            register={register}
            required
            error={errors}
            fileDetails={["documentName", "date", "remarks", "memorialNo"]}
          />

          <MultiFileRow
            label="CREATE_NEW_LOAN.FORM.DOCUMENT"
            name={CREATE_NEW_LOAN.DOCUMENT}
            control={control}
            setValue={setValue}
            register={register}
            required
            error={errors}
            fileDetails={["documentName", "date", "remarks", "memorialNo"]}
          />
        </div>
        <div className="d-flex justify-content-end gap-6 px-10 py-8 border-top">
          <Button size="sm" type="primary" htmlType="submit">
            {t("CREATE_NEW_LOAN.FORM.SAVE_BUTTON")}
          </Button>
        </div>
      </form>
    </>
  );
};
